import { GetLootboxV2GamesQuery } from '@/__generated__/graphql'
import { GET_LOOTBOX_V2_GAMES, useGetUserProfileByAddress } from '@/api/query'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { Flex, Skeleton, Text, Box } from '@chakra-ui/react'
import { IoMdCloseCircleOutline } from 'react-icons/io'
import { useQuery as useAppoloQuery } from '@apollo/client'
import Image from 'next/image'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { getTokenByDenom } from '@/services/token-service'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'
import { USDC_NEW_DENOM } from '@/constants/constants'
import { getChanceBgColor } from '@/helpers/getChanceColor'
import { GradientText } from '@/components/atoms/GradientText/GradientText'
import { AnimatePresence, motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { useLootboxContext } from '@/context/LootboxProvider'
import Link from 'next/link'
import { BigNumberInWei } from '@injectivelabs/utils'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { LootboxType } from '../../templates/transformLootboxRewards'
import { useTranslation } from 'next-i18next/pages'

type Game = {
  rewardId: string
  bet: string
  denom: string
  lootboxId: string
  player: string
  reward: string
  betUsd?: string
}

export const LastWinsWidget = ({ polling = false }: { polling?: boolean }) => {
  const { t } = useTranslation()
  const { data, loading, error } = useAppoloQuery(GET_LOOTBOX_V2_GAMES, {
    pollInterval: polling ? 5_000 : 0,
  })

  const games = transformLootboxUnlimitedGames(data)
  const gamesCount = Number(data?.statistic?.lootboxBetCount).toLocaleString('en-US', {
    maximumFractionDigits: 0,
  })

  return (
    <Box flexBasis={{ base: 'auto', md: 'full' }} h={'178px'} w={'full'} pos={'relative'} overflow={'hidden'}>
      <Flex mb={3} fontWeight={700} gap={3} alignItems={'center'}>
        <Text whiteSpace={'nowrap'}>{t('common.last-drop')}</Text>
        {loading && (
          <Skeleton mt={1} h={4} w={'130px'} startColor={'ninja.500'} endColor={'ninja.600'} rounded={'xl'} />
        )}
        {data && (
          <Text
            mt={1}
            lineHeight={1}
            display={{ base: 'block', sm: 'none', md: 'block' }}
            fontSize={'xs'}
            color={'whiteAlpha.600'}
          >
            {t('lootbox.opened-cases', { count: gamesCount })}
          </Text>
        )}
      </Flex>

      {loading && <LastWinsSkeleton />}
      {error && <LastWinsFallback />}
      {data && <LastWins games={games} />}
      <Box
        pos={'absolute'}
        bgGradient={`linear-gradient(270deg, #13133C 0%, transparent 100%)`}
        w={14}
        h={'142px'}
        right={0}
        bottom={0}
      />
    </Box>
  )
}

const LastWinsFallback = () => {
  const { t } = useTranslation()
  return (
    <Box p={3} bg={'ninja.700'} rounded={'2xl'}>
      <Flex h={'118px'} gap={1} justifyContent={'center'} flexDir={'column'} alignItems={'center'}>
        <Box color="ninja.400">
          <IoMdCloseCircleOutline size={40} />
        </Box>
        <Text textAlign={'center'} fontWeight={700} color={'ninja.400'}>
          {t('messages.error-last-wins')}
        </Text>
      </Flex>
    </Box>
  )
}

const LastWinsSkeleton = () => {
  return (
    <Flex pos={'absolute'} gap={2}>
      <Skeleton h={'142px'} w={'142px'} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
      <Skeleton h={'142px'} w={'142px'} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
      <Skeleton h={'142px'} w={'142px'} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
      <Skeleton h={'142px'} w={'142px'} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
      <Skeleton h={'142px'} w={'142px'} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
      <Skeleton h={'142px'} w={'142px'} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
      <Skeleton h={'142px'} w={'142px'} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
      <Skeleton h={'142px'} w={'142px'} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
      <Skeleton h={'142px'} w={'142px'} startColor="ninja.500" endColor="ninja.600" rounded={'2xl'} />
    </Flex>
  )
}

const LastWins = ({ games }: { games: Array<Game> }) => {
  const [hasRendered, setHasRendered] = useState(false)

  useEffect(() => {
    setHasRendered(true)
  }, [])

  return (
    <Flex pos={'absolute'}>
      <motion.ul style={{ display: 'flex', flexDirection: 'row-reverse', gap: '8px', listStyle: 'none' }}>
        <AnimatePresence>
          {games.map((game) => (
            <LastWinsGame key={game.rewardId} game={game} initialAnimation={hasRendered} />
          ))}
        </AnimatePresence>
      </motion.ul>
    </Flex>
  )
}

const LastWinsGame = ({ game, initialAnimation }: { game: Game; initialAnimation: boolean }) => {
  const { getLootboxCollectionById, getLootboxImagePrefix } = useLootboxContext()
  const collection = getLootboxCollectionById(game.lootboxId)
  const [isFlipped, setIsFlipped] = useState(false)
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const isUsdcToken = getTokenByDenom(game.denom)?.currency === SupportedCurrencies.USDC
  const useFiatEquivalent = !isUsd && !isUsdcToken && !!game.betUsd

  const handleMouseEnter = () => {
    setIsFlipped(true)
  }

  const handleMouseLeave = () => {
    setIsFlipped(false)
  }

  if (!collection?.rewards[game.reward] || collection.type === LootboxType.LIMITED) {
    return null
  }

  const reward = collection?.rewards[game.reward]

  return (
    <motion.li
      initial={initialAnimation ? { opacity: 0, y: -20 } : false}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      style={{ width: '142px', height: '142px', position: 'relative' }}
    >
      <motion.div
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.2 }}
        style={{ backfaceVisibility: 'hidden', position: 'absolute' }}
        initial={false}
      >
        <Box
          boxShadow={`0px 8px 40px 0px ${getChanceBgColor(reward.chance)} inset;`}
          p={2}
          h={'142px'}
          w={'142px'}
          bg={'ninja.700'}
          rounded={'xl'}
        >
          <UserProfile address={game.player} />
          <Flex flexDir={'column'} alignItems={'center'}>
            <Image
              width={64}
              height={64}
              src={getLootboxImagePrefix(collection.slug) + reward.image[game.denom]}
              alt=""
            />
            <FiatOrCryptoAmount
              iconSize={'18px'}
              wrapperStyles={{
                fontSize: 'sm',
              }}
              value={
                useFiatEquivalent
                  ? new TokenMoney(new BigNumberInWei(reward.multiplier).multipliedBy(game.betUsd!), USDC_NEW_DENOM)
                  : new TokenMoney(new BigNumberInWei(reward.multiplier).multipliedBy(game.bet), game.denom)
              }
            />
          </Flex>
        </Box>
      </motion.div>
      <motion.div
        animate={{ rotateY: isFlipped ? 0 : 180 }}
        transition={{ duration: 0.2 }}
        style={{ backfaceVisibility: 'hidden', position: 'absolute', transform: 'rotateY(-180deg)' }}
        initial={false}
      >
        <Link href={'/lootbox/' + collection.slug}>
          <Flex
            boxShadow={`0px 8px 40px 0px ${getChanceBgColor(reward.chance)} inset;`}
            p={2}
            h={'142px'}
            w={'142px'}
            bg={'ninja.700'}
            rounded={'xl'}
            flexDir={'column'}
            alignItems={'center'}
          >
            <UserProfile address={game.player} />
            <Box width={'64px'} height={'64px'}>
              <Image
                width={64}
                height={64}
                src={getLootboxImagePrefix(collection.slug) + collection.image.src}
                alt=""
              />
            </Box>
            <Text fontSize={'sm'} fontWeight={700} textAlign={'center'}>
              {collection.metadata.title}
            </Text>
          </Flex>
        </Link>
      </motion.div>
    </motion.li>
  )
}

const UserProfile = ({ address }: { address: string }) => {
  const { data: user } = useGetUserProfileByAddress(address)
  const modal = useAppModal()
  const numberOfNfts = user?.nfts?.totalCount
  const isNftOwner = Boolean(numberOfNfts)

  return (
    <Flex
      mb={1.5}
      gap={1}
      alignItems={'center'}
      cursor={'pointer'}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        modal.openModalNew(AppModals.UserStats, { address })
      }}
      w={'full'}
    >
      <AvatarCircle size={'xs'} src={user?.user?.profileAvatar} />
      {isNftOwner ? (
        <GradientText
          numberOfNfts={numberOfNfts!}
          props={{
            fontWeight: 600,
            fontSize: 'xs',
            wordBreak: 'keep-all',
            flexShrink: 0,
          }}
        >
          {user?.user?.profileUserName}
        </GradientText>
      ) : (
        <Text flexShrink={0} wordBreak={'keep-all'} fontWeight={700} fontSize={'xs'}>
          {user?.user?.profileUserName}
        </Text>
      )}
    </Flex>
  )
}

const transformLootboxUnlimitedGames = (data?: GetLootboxV2GamesQuery) => {
  if (!data?.lootboxUnlimitedGames?.edges.length) {
    return []
  }

  return data.lootboxUnlimitedGames.edges
    .map(({ node }) => {
      return node!.rewards.map((reward, index) => ({
        rewardId: node!.id + '-' + index,
        bet: (node!.bet as string) || '0',
        denom: node!.denom,
        lootboxId: node!.lootbox,
        player: node!.player,
        reward: reward!,
        betUsd: node!.betUsd != null ? String(node!.betUsd) : undefined,
      }))
    })
    .flat()
    .reverse()
    .slice(-18)
}
